import React, {
    memo,
    useContext,
    useState,
    useRef,
    useEffect,
    useMemo,
    useCallback,
    Fragment,
} from 'react';

import {
    Icon,
    SelectWrapper,
    Chip,
    Input,
    Select,
    Slider,
    FieldGroup,
    Text,
    Tooltip,
    Button,
    createUseStyles,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { isEqual } from '@Sage/ssm-utils';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { useWeb5Context } from 'lib/Web5Context';
import { getCrossID } from 'utils/filters';
import { getRange } from 'utils/time';

import {
    rangeFields,
    defaultPlaceholders,
    defaultValues,
    customFieldsComponent,
} from '../Filters/constants';
import { FiltersContext } from '../Filters/Context';
import useAdvancedFilters from '../Filters/hooks/useAdvancedFilters';
import useFilters from '../Filters/hooks/useFilters';
import { operators } from '../Filters/operators.js';
import useAddModal from '../Filters/useAddModal';
import ModalOptions from '../ModalOptions';
import ScrollBarsCustom from './ScrollBarsCustom';
import styles from './styles';
const {
    EMPTY,
    NOT_EMPTY,
    GREATER,
    GREATER_OR_EQUAL,
    LESS,
    LESS_OR_EQUAL,
    TODAY,
    YESTERDAY,
    TOMORROW,
    THIS_WEEK,
    LAST_WEEK,
    NEXT_WEEK,
    THIS_MONTH,
    LAST_MONTH,
    NEXT_MONTH,
    THIS_QUARTER,
    LAST_QUARTER,
    NEXT_QUARTER,
    THIS_YEAR,
    LAST_YEAR,
    NEXT_YEAR,
    IN,
    NOT_IN,
} = operators;

const useStyles = createUseStyles(styles, 'QuickFilters');

const inputFields = ['text', 'integer', 'decimal', 'currency', 'percent'];

const QuickFilters = memo(
    ({
        className,
        useQuickView,
        filters = [],
        schema,
        onChange,
        onClearAll,
        values,
        views,
        selectedView,
        customView,
        onChangeView,
        onChangeCustomView,
        hasFiltersActive,
        getCanClearAll,
        filterDragging,
        chipSize,
        isSubfilter,
        onChangeOperator,
        operators = {},
        license,
        crossOperators,
        crossLabels,
        entity,
        defaultFilters,
        extraSchema,
        extraMultiSchema,
        onConfirmAdd,
        onDeleteMultiple,
        onResetVisibles,
        visibleFilters,
        selectorsSchema,
        labels = {},
        showAdd,
        useAddIcon,
        getAllowedOperators = () => {},
        getFieldsWithoutAdvancedFilters = () => {},
        getHasAdvancedFilters = () => {},
        onToggle,
        toggled = {},
        forceSelectAllTypes,
        entityToForceLabel,
    }) => {
        const classes = useStyles();
        const [openDropdowns, setOpenDropdowns] = useState({});
        const [innerValues, setInnerValues] = useState(values || {});
        const oldInnerValues = useRef(null);
        const selectRefs = useRef({});
        const inputRefs = useRef({});
        const activeEditing = useRef({});
        const theme = useTheme();
        const [wrapperOverlayInnerStyle, setWrapperOverlayInnerStyle] = useState({
            maxHeight: window.innerHeight - 50,
            overflowY: 'auto',
        });

        const { t, language } = useWeb5Context();
        const { withActiveAdvancedFilters, setClearFilters, setWithActiveAdvancedFilters } =
            useContext(FiltersContext);

        const { hasFiltersApplied, renderFilters, renderSubfilters } = useFilters({
            classes,
            crossOperators,
            entity,
            getAllowedOperators,
            getFieldsWithoutAdvancedFilters,
            getHasAdvancedFilters,
            onToggle,
            operators,
            toggled,
            values,
            fromQuickFilters: true,
        });

        const { getFieldOperator, getOperatorValues, hasAdvancedFilters } = useAdvancedFilters({
            operators,
            crossOperators,
            getAllowedOperators,
            getFieldsWithoutAdvancedFilters,
            getHasAdvancedFilters,
        });
        const { onToggleModal, ...modalOptionsProps } = useAddModal({
            entity,
            defaultFilters,
            extraSchema,
            extraMultiSchema,
            onConfirmAdd,
            onDeleteMultiple,
            onResetVisibles,
            visibleFilters,
            selectorsSchema,
            labels,
            forceSelectAllTypes,
        });

        const classNames = classnames(classes.root, className);

        useEffect(() => {
            window.addEventListener('resize', () => {
                setWrapperOverlayInnerStyle({
                    maxHeight: window.innerHeight - 50,
                    overflowY: 'auto',
                });
            });
        }, []);

        useEffect(() => {
            if (!isEqual(oldInnerValues?.current, values)) {
                setInnerValues((currentInnerValues) => {
                    // Preserve values that are being actively edited
                    const updatedValues = { ...values };
                    Object.keys(activeEditing.current).forEach((fieldId) => {
                        if (
                            activeEditing.current[fieldId] &&
                            currentInnerValues[fieldId] !== undefined
                        ) {
                            updatedValues[fieldId] = currentInnerValues[fieldId];
                        }
                    });
                    return updatedValues;
                });
            }
        }, [values]);

        const finalSchema = useMemo(() => {
            return schema.filter((current) => {
                const ID = current.crossId || current.id;
                return filters.includes(ID);
            });
        }, [schema, filters]);

        const getIsOpen = useCallback(
            (fieldId) => {
                return (isOpen) => {
                    setOpenDropdowns({ ...openDropdowns, [fieldId]: isOpen });
                    // Clear active editing flag when closing dropdown
                    if (!isOpen && activeEditing.current[fieldId]) {
                        delete activeEditing.current[fieldId];
                    }
                };
            },
            [openDropdowns, setOpenDropdowns],
        );

        const getIsOpenValueList = useCallback(
            (fieldId) => {
                return (isOpen) => {
                    setOpenDropdowns({ ...openDropdowns, [fieldId]: isOpen });
                    // Clear active editing flag when closing dropdown
                    if (!isOpen && activeEditing.current[fieldId]) {
                        delete activeEditing.current[fieldId];
                    }
                    setTimeout(() => {
                        if (isOpen && selectRefs?.current?.[fieldId]) {
                            selectRefs.current[fieldId].focus();
                        }
                    }, 50);
                };
            },
            [openDropdowns, setOpenDropdowns],
        );

        const getIsOpenInput = useCallback((fieldId) => {
            return (isOpen) => {
                // Clear active editing flag when closing dropdown
                if (!isOpen && activeEditing.current[fieldId]) {
                    delete activeEditing.current[fieldId];
                }
                setTimeout(() => {
                    if (isOpen && inputRefs?.current?.[fieldId]) {
                        inputRefs.current[fieldId].focus();
                    }
                }, 50);
            };
        }, []);

        const commonOnChange = useCallback(
            ({ field, value, info, isGroupChange, operatorValue, crossEntity }) => {
                let newValue = value;
                //oldInnerValues to control wether we need to reset "innerValues" or not with "values" coming from props
                //we do it before any format process because we need those values to be equal to those that come from props

                const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
                const crossId = getCrossID(field.id, finalEntity);
                const advancedFilters = getHasAdvancedFilters(finalEntity);

                if (advancedFilters && field.dataType === 'probability') {
                    const currentOperators = !!finalEntity
                        ? crossOperators?.[finalEntity] || {}
                        : operators;

                    const currentOperatorValue = currentOperators[crossId]
                        ? currentOperators[crossId].value
                        : undefined;

                    const newOperatorValue = operatorValue || currentOperatorValue;
                    const oldValue = innerValues[crossId];

                    let newMinValue = field.inputProps.min;
                    let newMaxValue = field.inputProps.max;

                    if ([LESS, LESS_OR_EQUAL].includes(newOperatorValue)) {
                        newMinValue = Array.isArray(newValue) ? newValue[0] : newValue;
                        newMaxValue = oldValue ? oldValue[1] : newMaxValue;
                        newValue = [newMinValue, newMaxValue];
                    }
                    if ([GREATER, GREATER_OR_EQUAL].includes(newOperatorValue)) {
                        newMinValue = oldValue ? oldValue[0] : newMinValue;
                        newMaxValue = Array.isArray(newValue) ? newValue[1] : newValue;
                        newValue = [newMinValue, newMaxValue];
                    }
                }

                oldInnerValues.current = { ...innerValues, [crossId]: newValue };

                // We work with "innerValues" over "values" always we can.
                // That way we prevent unnecessary rerenders.
                setInnerValues({
                    ...innerValues,
                    [crossId]: newValue,
                });

                if (isGroupChange) {
                    if (
                        value.filter((current) => current).length > 0 &&
                        info?.action &&
                        info?.action === 'clear'
                    ) {
                        return;
                    }
                }

                //Input fields only execute onChange when onBlur to make it more efficient
                if (
                    inputFields.includes(field.dataType) &&
                    !!newValue &&
                    !info?.action &&
                    info?.action !== 'clear'
                ) {
                    return;
                }

                onChange({ filter: field, value: newValue, info, crossEntity: finalEntity });
            },
            [getHasAdvancedFilters, innerValues, onChange, crossOperators, operators],
        );

        const handleOnChangeGroup = useCallback(
            (field, crossEntity) => {
                const finalCrossEntity =
                    !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
                return (groupValue, value, index, event, info) => {
                    commonOnChange({
                        field,
                        value: groupValue,
                        info,
                        isGroupChange: true,
                        crossEntity: finalCrossEntity,
                    });
                };
            },
            [commonOnChange],
        );

        const handleOnChange = useCallback(
            ({ field, operatorValue, crossEntity }) => {
                const finalCrossEntity =
                    !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
                return (value, e, info) => {
                    const crossId = getCrossID(field.id, finalCrossEntity);

                    // Mark text fields as actively editing
                    if (inputFields.includes(field.dataType)) {
                        activeEditing.current[crossId] = true;
                    }

                    commonOnChange({
                        field,
                        value,
                        info,
                        operatorValue,
                        crossEntity: finalCrossEntity,
                    });
                };
            },
            [commonOnChange],
        );

        const valueListHandleOnChange = useCallback(
            (field, isMulti, crossEntity) => {
                const crossId = getCrossID(field.id, crossEntity);
                return (value, info) => {
                    if (isMulti && !value && selectRefs?.current?.[crossId]) {
                        setTimeout(() => {
                            selectRefs.current[crossId].focus();
                        }, 50);
                    }
                    commonOnChange({ field, value, info, crossEntity });
                };
            },
            [commonOnChange],
        );

        const handleChangeOperator = useCallback(
            ({ field, defaultValue, crossEntity }) =>
                (value) => {
                    const newValue = value || defaultValue;
                    onChangeOperator &&
                        onChangeOperator({ filter: field, value: newValue, crossEntity });
                    if ([EMPTY, NOT_EMPTY].includes(newValue.value)) {
                        handleOnChange({ field, crossEntity })(defaultValues[field.dataType]);
                    } else if (field.dataType === 'date') {
                        handleOnChange({ field, crossEntity })(defaultValues[field.dataType]);
                    }
                },
            [handleOnChange, onChangeOperator],
        );

        const handleOnClearFilter = useCallback(
            (field, operatorDefaultValue, crossEntity) => {
                return (e) => {
                    e.stopPropagation();

                    const finalEntity =
                        !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
                    const crossId = getCrossID(field.id, finalEntity);

                    // Clear active editing flag when clearing filter
                    if (activeEditing.current[crossId]) {
                        delete activeEditing.current[crossId];
                    }

                    commonOnChange({ field, value: defaultValues[field.dataType], crossEntity });
                    operatorDefaultValue &&
                        handleChangeOperator({ field, crossEntity })(operatorDefaultValue);
                };
            },
            [commonOnChange, handleChangeOperator],
        );

        const handleOnBlur = useCallback(
            (field, crossEntity) => {
                return (value) => {
                    let operatorValue;

                    const finalEntity =
                        !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
                    const crossId = getCrossID(field.id, finalEntity);
                    const advancedFilters = getHasAdvancedFilters(finalEntity);

                    // Clear active editing flag
                    if (activeEditing.current[crossId]) {
                        delete activeEditing.current[crossId];
                    }

                    if (advancedFilters) {
                        const currentOperators = !!finalEntity
                            ? crossOperators?.[finalEntity] || {}
                            : operators;
                        operatorValue = currentOperators[field.id]
                            ? currentOperators[field.id].value
                            : undefined;
                    }

                    if (rangeFields[field.dataType]) {
                        const innerValue = innerValues[crossId]?.filter(
                            (current) => current,
                        ).length;

                        if (
                            innerValue === 0 ||
                            innerValue === 2 ||
                            (operatorValue &&
                                innerValue > 0 &&
                                [LESS, LESS_OR_EQUAL, GREATER, GREATER_OR_EQUAL].includes(
                                    operatorValue,
                                ))
                        ) {
                            return onChange({
                                filter: field,
                                value: innerValues[crossId] || null,
                                crossEntity,
                            });
                        }
                        return;
                    }

                    return onChange({ filter: field, value, crossEntity });
                };
            },
            [getHasAdvancedFilters, onChange, crossOperators, operators, innerValues],
        );

        const renderChip = useCallback(
            ({
                isOpen,
                finalValue,
                actionValues,
                field,
                showFilterNumber = true,
                showSelectedValue,
                className,
                onRemove,
                overrides,
                isDate,
                entity,
            }) => {
                let numberFilters = '';
                let selectedValue = '';
                let isEmpty = false;
                let chipLabel = field.label;

                let crossLabelEntity =
                    !!entity && crossLabels?.[entity] ? `(${crossLabels[entity]})` : null;

                if (!crossLabelEntity && entityToForceLabel?.entity) {
                    crossLabelEntity = `(${crossLabels[entityToForceLabel.entity]})`;
                }

                if (!!crossLabelEntity) chipLabel = `${chipLabel} ${crossLabelEntity}`;

                if (isDate) isEmpty = finalValue.every((el) => el === null);

                if (finalValue && showFilterNumber) {
                    if (Array.isArray(finalValue)) {
                        numberFilters = finalValue.length;
                    } else {
                        numberFilters = 1;
                    }
                }

                if (finalValue && showSelectedValue) {
                    selectedValue = Array.isArray(finalValue)
                        ? finalValue[0].label
                        : finalValue.label;
                }

                if (numberFilters && showSelectedValue) {
                    chipLabel = !!crossLabelEntity
                        ? `${selectedValue} ${crossLabelEntity}`
                        : selectedValue;
                } else if (numberFilters && numberFilters > 1 && !isDate) {
                    chipLabel = !!crossLabelEntity
                        ? `${field.label} ${crossLabelEntity} (${numberFilters})`
                        : `${field.label} (${numberFilters})`;
                } else if (numberFilters && numberFilters === 1 && !isDate) {
                    const chipLabelInner = finalValue[0]?.label || finalValue?.label;
                    chipLabel = (
                        <span className={classes.singleSelected}>
                            {!!crossLabelEntity
                                ? `${chipLabelInner} ${crossLabelEntity}`
                                : chipLabelInner}
                        </span>
                    );
                } else if (isDate && !isEmpty) {
                    const chipLabelInner =
                        field?.operator?.label ||
                        field?.operator?.value ||
                        t('label_advanced_filter_option_inside_range');
                    chipLabel = (
                        <span className={classes.singleSelected}>
                            {!!crossLabelEntity
                                ? `${chipLabelInner} ${crossLabelEntity}`
                                : chipLabelInner}
                        </span>
                    );
                }

                if (overrides?.CloseIcon?.getClassName) {
                    overrides.CloseIcon.className =
                        overrides.CloseIcon.getClassName() || overrides.CloseIcon.className || '';
                }

                if (overrides?.DividerIcon?.getClassName) {
                    overrides.DividerIcon.className =
                        overrides.DividerIcon.getClassName() ||
                        overrides.DividerIcon.className ||
                        '';
                }

                let isActive = isOpen || false;
                let isFilled = Array.isArray(finalValue)
                    ? finalValue?.length
                    : !!finalValue || false;

                if (rangeFields[field.dataType]) isFilled = finalValue?.some(Boolean);

                // Force filled state if dragging
                if (field.id && field.id === filterDragging) {
                    isActive = true;
                    isFilled = true;
                }

                const advancedFilters = getHasAdvancedFilters(entity);

                const hasOperator =
                    advancedFilters &&
                    !!field.operator &&
                    ![IN, NOT_IN].includes(field?.operator?.value);

                const isOutlinedColor = isSubfilter || false;

                const chipComp = (
                    <Chip
                        size={chipSize || 'large'}
                        isUnfolded={true}
                        isActive={isActive}
                        isFilled={!!isFilled || hasOperator || actionValues?.length > 0}
                        isOutlinedColor={isOutlinedColor}
                        className={className}
                        onRemove={onRemove}
                        overrides={overrides}
                    >
                        {chipLabel}
                    </Chip>
                );

                if (license && license.includes('free') && field?.id === 'view') {
                    return (
                        <Tooltip placement="top" content={t('helptext_views_accounts')}>
                            {chipComp}
                        </Tooltip>
                    );
                } else if ((numberFilters && numberFilters === 1) || (isDate && !isEmpty)) {
                    let tooltipLabel = field.label;
                    if (!!entity && crossLabels?.[entity])
                        tooltipLabel = `${field.label} (${crossLabels[entity]})`;
                    return (
                        <Tooltip placement="top" content={tooltipLabel}>
                            {chipComp}
                        </Tooltip>
                    );
                } else {
                    return chipComp;
                }
            },
            [
                crossLabels,
                filterDragging,
                getHasAdvancedFilters,
                isSubfilter,
                chipSize,
                license,
                classes.singleSelected,
                t,
                entityToForceLabel,
            ],
        );

        const selectOverrides = useMemo(
            () => ({
                styles: {
                    menu: (base) => ({
                        ...base,
                        position: 'relative',
                        boxShadow: 'none !important',
                        margin: 0,
                        paddingBottom: 0,
                    }),
                    menuList: (base) => ({
                        ...base,
                        paddingBottom: 0,
                        ...theme.utils.scrollbar,
                    }),
                },
                option: {
                    style: {
                        borderRadius: 4,
                    },
                },
            }),
            [theme],
        );

        const renderField = useCallback(
            (field, crossEntity, subfilterSchema) => {
                if (!field.inputProps) field.inputProps = {};
                const fieldInputProps = { ...field.inputProps };
                if (Object.prototype.hasOwnProperty.call(fieldInputProps, 'key')) {
                    delete fieldInputProps.key;
                }

                const finalEntity = !!crossEntity && !field.isFakeCrossFilter ? crossEntity : null;
                const crossId = getCrossID(field.id, finalEntity);
                const advancedFilters = getHasAdvancedFilters(finalEntity);

                let commonProps = {
                    labelMode: 'vertical',
                    ...fieldInputProps,
                };

                let FilterField;
                let OperatorField = null;

                let operatorValue = undefined;
                let operatorDefaultValue = undefined;

                let defaultValue = defaultValues[field.dataType];

                if (rangeFields[field.dataType]) defaultValue = defaultValues['range'];

                // There are some actions that can activate filters and we need
                // to control the chip ui also for these filters
                const actionValues =
                    (commonProps?.actions?.length &&
                        commonProps.actions.reduce((arr, current) => {
                            if (innerValues[current.id]) {
                                arr.push({
                                    id: current.id,
                                    value: innerValues[current.id],
                                });
                            }
                            return arr;
                        }, [])) ||
                    null;

                let finalValue = innerValues?.[crossId] || defaultValue;

                const isOpenDropDown = openDropdowns?.[crossId] || false;

                if (hasAdvancedFilters({ ...field, crossEntity: finalEntity })) {
                    operatorValue = getFieldOperator(field, finalEntity);

                    const {
                        options,
                        value,
                        defaultValue: defaultValueTemp,
                    } = getOperatorValues(field.dataType, operatorValue, finalEntity);

                    operatorDefaultValue = defaultValueTemp;

                    const isDefaultOperator = value.value === operatorDefaultValue.value;

                    const overrides = isDefaultOperator
                        ? {
                              control: {
                                  style: {
                                      borderColor: `${theme.colors.grey[100]} !important`,
                                      backgroundColor: theme.colors.primary.white,
                                  },
                              },
                          }
                        : {
                              control: {
                                  style: {
                                      borderColor: 'transparent !important',
                                      backgroundColor: theme.colors.semantic.infoCustom50,
                                      '&:hover': {
                                          backgroundColor: theme.colors.semantic.infoCustom100,
                                      },
                                      cursor: 'pointer',
                                  },
                              },
                              controlFocused: {
                                  style: {
                                      borderColor: 'transparent !important',
                                      backgroundColor: theme.colors.semantic.infoCustom100,
                                  },
                              },
                              indicatorsContainer: {
                                  style: {
                                      '& svg path': {
                                          fill: `${theme.colors.semantic.info500} !important`,
                                      },
                                      '& .hoi-poi-select__indicator-separator': {
                                          backgroundColor: theme.colors.semantic.info500,
                                      },
                                  },
                              },
                              valueContainer: {
                                  style: {
                                      '& .hoi-poi-select__single-value': {
                                          color: theme.colors.semantic.info500,
                                      },
                                  },
                              },
                          };

                    const operatorFieldProps = {
                        isRequired: isDefaultOperator,
                        isSearchable: false,
                        isFullWidth: true,
                        className: classnames('fm-adv-filter-operator', classes.advancedFilter),
                        clearIcon: !isDefaultOperator ? (
                            <Icon name="refresh" size="medium" />
                        ) : null,
                        onChange: handleChangeOperator({
                            field,
                            defaultValue: operatorDefaultValue,
                            crossEntity: finalEntity,
                        }),
                        options,
                        value,
                        inputProps: { name: 'search' },
                        overrides,
                    };

                    OperatorField = <Select key={`${crossId}__operator`} {...operatorFieldProps} />;
                }

                const isDisabled =
                    advancedFilters &&
                    [EMPTY, NOT_EMPTY, LAST_WEEK, LAST_MONTH].includes(operatorValue);

                const subfiltersProps = {
                    className: classes.subFilters,
                    handleChangeOperator,
                    handleOnBlur,
                    handleOnChange,
                    handleOnChangeGroup,
                    innerValues,
                    parentFilter: crossId,
                    renderer: renderFilters,
                    schema: subfilterSchema,
                    toggled: toggled?.[crossId]?.subfilters || {},
                };

                if (field.overrideDefaultComponent) {
                    const FieldComponent = customFieldsComponent[field.overrideDefaultComponent];

                    const fieldComponentProps = {
                        crossEntity: finalEntity,
                        field,
                        getRef: (ref) => {
                            selectRefs.current[crossId] = ref;
                        },
                        values: innerValues,
                        onChange: handleOnChange,
                        onBlur: handleOnBlur,
                        operator: operatorValue,
                        isQuickfilter: true,
                    };

                    const onRemove =
                        (Array.isArray(finalValue) && finalValue.length === 0) || !finalValue
                            ? null
                            : handleOnClearFilter(field, operatorDefaultValue, finalEntity);

                    FilterField = (
                        <SelectWrapper
                            className={classes.selectWrapper}
                            classes={{ Popover: classes.popoverCustomWide }}
                            getIsOpen={getIsOpenValueList(crossId)}
                            overlayInnerStyle={wrapperOverlayInnerStyle}
                            customOptions={
                                <Fragment>
                                    {OperatorField}
                                    <FieldComponent key={crossId} {...fieldComponentProps} />
                                </Fragment>
                            }
                        >
                            {renderChip({
                                isOpen: isOpenDropDown,
                                finalValue,
                                actionValues,
                                field,
                                onRemove,
                                overrides: commonProps.overrides?.chip || {},
                                entity: finalEntity,
                            })}
                        </SelectWrapper>
                    );
                } else {
                    switch (field.dataType) {
                        case 'bool':
                            FilterField = (
                                <SelectWrapper
                                    className={classes.selectWrapper}
                                    getIsOpen={getIsOpen(crossId)}
                                    customOptions={
                                        <Fragment>
                                            {OperatorField}
                                            <Select
                                                {...commonProps}
                                                value={finalValue}
                                                onChange={handleOnChange({
                                                    field,
                                                    crossEntity: finalEntity,
                                                })}
                                                isFullWidth
                                                labelMode="horizontal"
                                                isReadOnly={isDisabled}
                                            />
                                            {renderSubfilters(subfiltersProps)}
                                        </Fragment>
                                    }
                                >
                                    {renderChip({
                                        isOpen: isOpenDropDown,
                                        finalValue,
                                        field,
                                        showFilterNumber: false,
                                        entity: finalEntity,
                                    })}
                                </SelectWrapper>
                            );
                            break;
                        case 'probability':
                            const isLessOperator = [LESS, LESS_OR_EQUAL].includes(operatorValue);
                            const isGreaterOperator = [GREATER, GREATER_OR_EQUAL].includes(
                                operatorValue,
                            );
                            const isRange = !isLessOperator && !isGreaterOperator;
                            if (isLessOperator) {
                                finalValue = finalValue ? finalValue[0] : 0;
                            }
                            if (isGreaterOperator) {
                                finalValue = finalValue ? finalValue[1] : 100;
                            }
                            FilterField = (
                                <SelectWrapper
                                    className={classes.selectWrapper}
                                    classes={{ Popover: classes.popoverCustomWide }}
                                    getIsOpen={getIsOpen(crossId)}
                                    customOptions={
                                        <Fragment>
                                            {OperatorField}
                                            <Slider
                                                isFullWidth
                                                key={crossId}
                                                label={field.label}
                                                labelMode="vertical"
                                                onChange={handleOnChange({
                                                    field,
                                                    operatorValue,
                                                    crossEntity: finalEntity,
                                                })}
                                                value={finalValue}
                                                min={0}
                                                max={100}
                                                step={10}
                                                tipFormatter={(value) => `${value}%`}
                                                isRange={advancedFilters ? isRange : true}
                                                isReadOnly={isDisabled}
                                                {...field.inputProps}
                                            />
                                            {renderSubfilters(subfiltersProps)}
                                        </Fragment>
                                    }
                                >
                                    {renderChip({
                                        isOpen: isOpenDropDown,
                                        finalValue,
                                        field,
                                        onRemove: finalValue
                                            ? handleOnClearFilter(
                                                  field,
                                                  operatorDefaultValue,
                                                  finalEntity,
                                              )
                                            : null,
                                        showFilterNumber: false,
                                        entity: finalEntity,
                                    })}
                                </SelectWrapper>
                            );
                            break;
                        case 'singleValueList':
                        case 'multipleValueList':
                        case 'relatedValueList':
                            if (!commonProps.placeholder) {
                                commonProps.placeholder = defaultPlaceholders[field.dataType] || '';
                            }

                            let classNames = [classes.selectFuzzy];
                            if (commonProps.className) classNames.push(commonProps.className);

                            let finalOverrides = selectOverrides;
                            if (commonProps.overrides) {
                                finalOverrides = {
                                    ...finalOverrides,
                                    ...commonProps.overrides,
                                };
                            }

                            if (isDisabled) commonProps.placeholder = '';

                            if (!commonProps.highlightMatch && commonProps.isFuzzy)
                                commonProps.highlightMatch = commonProps.isFuzzy;

                            FilterField = (
                                <SelectWrapper
                                    className={classes.selectWrapper}
                                    classes={{ Popover: classes.popoverCustomWide }}
                                    getIsOpen={getIsOpenValueList(crossId)}
                                    overlayInnerStyle={wrapperOverlayInnerStyle}
                                    customOptions={
                                        <Fragment>
                                            {OperatorField}
                                            <Select
                                                {...commonProps}
                                                className={classNames.join(' ')}
                                                value={finalValue}
                                                onChange={valueListHandleOnChange(
                                                    field,
                                                    commonProps['isMulti'],
                                                    finalEntity,
                                                )}
                                                isFullWidth
                                                menuPosition="absolute"
                                                useMenuPortal={false}
                                                getRef={(ref) => {
                                                    selectRefs.current[crossId] = ref;
                                                }}
                                                overrides={finalOverrides}
                                                isReadOnly={isDisabled}
                                                inputProps={{ name: 'search' }}
                                            />
                                            {renderSubfilters(subfiltersProps)}
                                        </Fragment>
                                    }
                                >
                                    {renderChip({
                                        isOpen: isOpenDropDown,
                                        finalValue,
                                        actionValues,
                                        field,
                                        onRemove: finalValue
                                            ? handleOnClearFilter(
                                                  field,
                                                  operatorDefaultValue,
                                                  finalEntity,
                                              )
                                            : null,
                                        overrides: commonProps.overrides?.chip || {},
                                        entity: finalEntity,
                                    })}
                                </SelectWrapper>
                            );
                            break;
                        case 'text':
                        default:
                            commonProps.type = field.dataType;
                            commonProps.onBlur = handleOnBlur(field, finalEntity);
                            commonProps.isReadOnly = isDisabled;
                            if (!commonProps.placeholder) {
                                commonProps.placeholder = defaultPlaceholders[field.dataType] || '';
                            }

                            if (isDisabled) commonProps.placeholder = '';

                            FilterField = (
                                <SelectWrapper
                                    className={classes.selectWrapper}
                                    classes={{ Popover: classes.popoverCustomWide }}
                                    getIsOpen={getIsOpenInput(crossId)}
                                    customOptions={
                                        <Fragment>
                                            {OperatorField}
                                            <Input
                                                {...commonProps}
                                                value={finalValue}
                                                onChange={handleOnChange({
                                                    field,
                                                    crossEntity: finalEntity,
                                                })}
                                                onEnter={handleOnBlur(field, finalEntity)}
                                                isFullWidth
                                                ref={(ref) => {
                                                    inputRefs.current[field.id] = ref;
                                                }}
                                            />
                                            {renderSubfilters(subfiltersProps)}
                                        </Fragment>
                                    }
                                >
                                    {renderChip({
                                        isOpen: isOpenDropDown,
                                        finalValue,
                                        field,
                                        onRemove: finalValue
                                            ? handleOnClearFilter(
                                                  field,
                                                  operatorDefaultValue,
                                                  finalEntity,
                                              )
                                            : null,
                                        showFilterNumber: false,
                                        entity: finalEntity,
                                    })}
                                </SelectWrapper>
                            );
                            break;
                    }

                    if (rangeFields[field.dataType]) {
                        const Field = rangeFields[field.dataType];
                        if (field.dataType === 'date') {
                            finalValue = [finalValue[0] || null, finalValue[1] || null];
                        }

                        const commonRange = {
                            calendarButtonLabel: t('label_today'),
                            onBlur:
                                field.dataType !== 'date' ? handleOnBlur(field, finalEntity) : null,
                            placeholder:
                                commonProps.placeholder ||
                                defaultPlaceholders[field.dataType] ||
                                t('action_select'),
                            lang: language,
                        };

                        const inputOneIsReadOnly =
                            advancedFilters &&
                            [
                                EMPTY,
                                NOT_EMPTY,
                                TODAY,
                                YESTERDAY,
                                TOMORROW,
                                THIS_WEEK,
                                LAST_WEEK,
                                NEXT_WEEK,
                                THIS_MONTH,
                                LAST_MONTH,
                                NEXT_MONTH,
                                THIS_QUARTER,
                                LAST_QUARTER,
                                NEXT_QUARTER,
                                THIS_YEAR,
                                LAST_YEAR,
                                NEXT_YEAR,
                                GREATER,
                                GREATER_OR_EQUAL,
                            ].includes(operatorValue);

                        const inputTwoIsReadOnly =
                            advancedFilters &&
                            [
                                EMPTY,
                                NOT_EMPTY,
                                TODAY,
                                YESTERDAY,
                                TOMORROW,
                                THIS_WEEK,
                                LAST_WEEK,
                                NEXT_WEEK,
                                THIS_MONTH,
                                LAST_MONTH,
                                NEXT_MONTH,
                                THIS_QUARTER,
                                LAST_QUARTER,
                                NEXT_QUARTER,
                                THIS_YEAR,
                                LAST_YEAR,
                                NEXT_YEAR,
                                LESS,
                                LESS_OR_EQUAL,
                            ].includes(operatorValue);

                        const inputPropsOne = {
                            ...commonProps,
                            ...commonRange,
                            className: field.locators?.first || null,
                            isReadOnly: inputOneIsReadOnly,
                            overrides: {
                                flatpickrOptions: {
                                    position: 'auto center',
                                    static: true,
                                },
                            },
                        };

                        const inputPropsTwo = {
                            ...commonProps,
                            ...commonRange,
                            className: field.locators?.last || null,
                            isReadOnly: inputTwoIsReadOnly,
                            overrides: {
                                flatpickrOptions: {
                                    position: 'auto center',
                                    static: true,
                                },
                            },
                        };

                        if (inputOneIsReadOnly) inputPropsOne.placeholder = '';
                        if (inputTwoIsReadOnly) inputPropsTwo.placeholder = '';

                        if (field.dataType === 'date') {
                            const minDate = finalValue[0] || null;
                            const maxDate = finalValue[1] || null;
                            inputPropsOne.maxDate = maxDate ? new Date(maxDate) : maxDate;
                            inputPropsTwo.minDate = minDate ? new Date(minDate) : minDate;
                            inputPropsOne.maxDate?.setHours(0, 0, 0, 0);
                            inputPropsTwo.minDate?.setHours(0, 0, 0, 0);

                            if (
                                advancedFilters &&
                                [
                                    TODAY,
                                    YESTERDAY,
                                    TOMORROW,
                                    THIS_WEEK,
                                    LAST_WEEK,
                                    NEXT_WEEK,
                                    THIS_MONTH,
                                    LAST_MONTH,
                                    NEXT_MONTH,
                                    THIS_QUARTER,
                                    LAST_QUARTER,
                                    NEXT_QUARTER,
                                    THIS_YEAR,
                                    LAST_YEAR,
                                    NEXT_YEAR,
                                ].includes(operatorValue)
                            ) {
                                finalValue = getRange(operatorValue);
                                const currentValue = innerValues[crossId] || values[crossId];

                                // Trigger onChange to set the predefined filter values
                                // for the chosen operator if needed
                                if (
                                    !currentValue ||
                                    (!!currentValue.length &&
                                        currentValue[0].toString() !== finalValue[0].toString()) ||
                                    (!!currentValue.length &&
                                        currentValue[1].toString() !== finalValue[1].toString())
                                ) {
                                    handleOnChangeGroup(field, crossEntity)(finalValue);
                                }
                            }
                        }

                        return (
                            <div key={crossId} className={classes.field}>
                                <SelectWrapper
                                    className={classes.selectWrapper}
                                    classes={{ Popover: classes.popoverCustomWide }}
                                    getIsOpen={getIsOpen(crossId)}
                                    customOptions={
                                        <Fragment>
                                            {OperatorField}
                                            <FieldGroup
                                                key={crossId}
                                                inputs={[Field, Field]}
                                                isFullWidth
                                                dividerText="-"
                                                onChange={handleOnChangeGroup(field, finalEntity)}
                                                value={finalValue}
                                                inputProps={[inputPropsOne, inputPropsTwo]}
                                                classes={{
                                                    divider: classes.fieldGroupDivider,
                                                    rangeWrapper: classes.fieldGroupRangeWrapper,
                                                }}
                                            />
                                            {renderSubfilters(subfiltersProps)}
                                        </Fragment>
                                    }
                                >
                                    {renderChip({
                                        isOpen: isOpenDropDown,
                                        finalValue,
                                        field,
                                        onRemove: finalValue?.some(Boolean)
                                            ? handleOnClearFilter(
                                                  field,
                                                  operatorDefaultValue,
                                                  finalEntity,
                                              )
                                            : null,
                                        showFilterNumber: true,
                                        isDate: true,
                                        entity: finalEntity,
                                    })}
                                </SelectWrapper>
                            </div>
                        );
                    }
                }

                return (
                    <div key={crossId} className={classes.field}>
                        {FilterField}
                    </div>
                );
            },
            [
                getHasAdvancedFilters,
                innerValues,
                openDropdowns,
                hasAdvancedFilters,
                classes.subFilters,
                classes.field,
                classes.advancedFilter,
                classes.selectWrapper,
                classes.popoverCustomWide,
                classes.selectFuzzy,
                classes.fieldGroupDivider,
                classes.fieldGroupRangeWrapper,
                handleChangeOperator,
                handleOnBlur,
                handleOnChange,
                handleOnChangeGroup,
                renderFilters,
                toggled,
                getFieldOperator,
                getOperatorValues,
                theme,
                getIsOpen,
                renderSubfilters,
                renderChip,
                handleOnClearFilter,
                selectOverrides,
                getIsOpenValueList,
                wrapperOverlayInnerStyle,
                valueListHandleOnChange,
                getIsOpenInput,
                t,
                language,
                values,
            ],
        );

        const renderFields = useCallback(
            (label, fields, index, crossEntity, subfilterSchema) => {
                return (
                    <Fragment key={index}>
                        {fields?.length > 0 &&
                            fields.map((field) => {
                                if (!field.label) field.label = label;
                                return renderField(field, crossEntity, subfilterSchema);
                            })}
                    </Fragment>
                );
            },
            [renderField],
        );

        const clearFiltersClassName = useMemo(() => {
            if (getCanClearAll) {
                if (!getCanClearAll()) return classes.actionsClearDisabled;
            } else if (!hasFiltersApplied && !hasFiltersActive && !withActiveAdvancedFilters) {
                return classes.actionsClearDisabled;
            }

            return classes.actionsClear;
        }, [
            getCanClearAll,
            hasFiltersApplied,
            hasFiltersActive,
            withActiveAdvancedFilters,
            classes.actionsClear,
            classes.actionsClearDisabled,
        ]);

        const handleClearAll = useCallback(() => {
            if (getCanClearAll) {
                if (getCanClearAll()) {
                    onClearAll && onClearAll();
                    setClearFilters(true);
                    setWithActiveAdvancedFilters(false);
                }
            } else if (
                hasFiltersApplied ||
                selectedView ||
                hasFiltersActive ||
                withActiveAdvancedFilters
            ) {
                onClearAll && onClearAll();
                setClearFilters(true);
                setWithActiveAdvancedFilters(false);
            }
        }, [
            hasFiltersApplied,
            selectedView,
            hasFiltersActive,
            onClearAll,
            setClearFilters,
            getCanClearAll,
            withActiveAdvancedFilters,
            setWithActiveAdvancedFilters,
        ]);

        const handleClearView = useCallback(
            (e) => {
                e.stopPropagation();
                if (customView) {
                    onChangeCustomView(null);
                } else {
                    onChangeView(null);
                }
                setOpenDropdowns({ ...openDropdowns, idView: false });
            },
            [customView, onChangeCustomView, onChangeView, openDropdowns, setOpenDropdowns],
        );

        let viewsClasses = [classes.views];
        if (!filters || filters?.length === 0) viewsClasses.push(classes.viewsAlone);

        const viewsChipClasses = useMemo(
            () =>
                classnames(classes.viewsChip, {
                    [classes.viewsChipFilled]: customView || selectedView,
                }),
            [classes.viewsChip, classes.viewsChipFilled, customView, selectedView],
        );

        const hasActiveFilters = useMemo(() => {
            if (
                (values && Object.keys(values).length > 0) ||
                (operators && Object.keys(operators).length)
            )
                return true;

            const activeCrossOperators =
                (crossOperators &&
                    Object.entries(crossOperators).reduce((num, [entity, operators]) => {
                        if (Object.keys(operators).length > 0) num++;
                        return num;
                    }, 0)) ||
                0;

            if (activeCrossOperators > 0) return true;

            return false;
        }, [values, operators, crossOperators]);

        const addFiltersIconTooltip = useMemo(() => {
            let text = labels.addFields;
            if (labels?.addFields) return text;
            if (!visibleFilters?.length) text = t('label_add_filters');
            else text = t('label_edit_filters');
            return text;
        }, [visibleFilters, labels, t]);

        return (
            <div className={classNames}>
                <div className={classes.left}>
                    <ScrollBarsCustom>
                        {useQuickView && (
                            <div className={viewsClasses.join(' ')}>
                                <SelectWrapper
                                    className={classes.selectWrapper}
                                    classes={{ Popover: classes.popoverCustomWide }}
                                    options={views}
                                    getIsOpen={getIsOpen('idView')}
                                    value={selectedView}
                                    onChange={customView ? onChangeCustomView : onChangeView}
                                    truncateOptions={false}
                                >
                                    {renderChip({
                                        isOpen: openDropdowns?.['idView'] || false,
                                        finalValue: customView || selectedView || null,
                                        field: { id: 'view', label: t('label_view') },
                                        showSelectedValue: true,
                                        className: viewsChipClasses,
                                        isFilled: customView || selectedView || null,
                                        isFolded: true,
                                        onRemove:
                                            customView || selectedView ? handleClearView : null,
                                    })}
                                </SelectWrapper>
                            </div>
                        )}
                        {useQuickView && filters?.length > 0 && <div className={classes.divider} />}
                        {finalSchema.length > 0 &&
                            finalSchema.map((current, index) => {
                                return renderFields(
                                    current.label,
                                    current.fields,
                                    index,
                                    current.entity,
                                    current.subfilterSchema,
                                );
                            })}
                    </ScrollBarsCustom>
                    {onClearAll && finalSchema?.length !== 0 && (
                        <div className={classes.clearContainer}>
                            <Tooltip content={t('action_clear_filters')}>
                                <Text
                                    className={clearFiltersClassName}
                                    type="body"
                                    onClick={handleClearAll}
                                >
                                    {t('action_clear')}
                                </Text>
                            </Tooltip>
                        </div>
                    )}
                </div>
                {showAdd && !useAddIcon && (
                    <div className={classes.right}>
                        <div className={classes.addButton}>
                            <Button
                                isFullWidth
                                icon="plus"
                                type="secondary"
                                onClick={onToggleModal}
                            >
                                {labels?.addFields || t('label_add_filters')}
                            </Button>
                        </div>
                    </div>
                )}
                {showAdd && useAddIcon && (
                    <div className={classes.right}>
                        <div className={classes.addButtonIcon}>
                            <Icon
                                name={hasActiveFilters ? 'filterListActive' : 'filterList'}
                                size="large"
                                onClick={onToggleModal}
                                tooltip={addFiltersIconTooltip}
                            />
                        </div>
                    </div>
                )}

                <ModalOptions {...modalOptionsProps} onToggleModal={onToggleModal} />
            </div>
        );
    },
);

QuickFilters.propTypes = {
    className: PropTypes.string,
    useQuickView: PropTypes.bool,
    filters: PropTypes.array,
    schema: PropTypes.array,
    onChange: PropTypes.func,
    onClearAll: PropTypes.func,
    values: PropTypes.object,
    views: PropTypes.array,
    selectedView: PropTypes.object,
    customView: PropTypes.object,
    onChangeView: PropTypes.func,
    onChangeCustomView: PropTypes.func,
    hasFiltersActive: PropTypes.bool,
};

export default QuickFilters;
